import React, { useLayoutEffect, useRef, useState } from "react";
import { default as _groupBy } from "lodash-es/groupBy";

let firstRender = true;

type ListViewType = HTMLElement[][];
type NColType = HTMLElement[];
type ScrollableBaseType = [string, HTMLElement[]];
type ScrollableType = ScrollableBaseType[];

export const useCssFilter = (
  el,
  activeFilter,
  showLimit,
  groupBy,
  hasFilters,
  hasBeenToggled,
  type,
  finished,
  setFinished,
  disabled = false,
  mountHiddenMarkets: boolean | undefined = undefined,
  scrollType?
) => {
  const [elements, setElements] = useState({
    filteredElements: [],
    unfilteredElements: [],
  });
  const displayType = useRef<string>("");
  const allElements = useRef<NColType | ListViewType | ScrollableType | null>();
  const allElementsFlat = useRef<NColType | null>();

  let _activeFilter = activeFilter?.toLowerCase();
  let _showLimit =
    typeof showLimit === "string" ? showLimit.toLowerCase() : showLimit;
  if (_activeFilter === "list view") _activeFilter = "all";
  if (_showLimit === "list view") _showLimit = "all";

  const isListView = type === "slider" && _activeFilter === "all";
  const isScrollableType = type === "scrollable" && scrollType === "col";
  const isRowGroupedType = type === "rowgrouped";

  let filterType = `[data-filter-group]`;
  if (isListView) filterType = `[data-filter-group-set]`;
  if (isScrollableType || isRowGroupedType) filterType = `[data-filter-group-row]`;

  let filteredElements, unfilteredElements;
  
  useLayoutEffect(() => {
    if (!el || disabled) return;
    if (window && "document" in window) {
      // Get all the dom nodes (elements)
      // If Slider List View, else NColGrouped template
      if (isListView) {
        const elementColumns = Array.from(
          el.querySelectorAll(`${filterType}`) as HTMLElement[]
        );
        allElements.current = elementColumns.map((element) =>
          Array.from(element.querySelectorAll(`[data-filter-group]`))
        ) as HTMLElement[][];
      } else if (isScrollableType) {
        // Get all the DOM Nodes that have the [data-filter-group-row] data attribute
        const elementRows = el.querySelectorAll(`${filterType}`);
        // Group them together e.g. { 1: [node, node, node] }
        const groupedElementRows = _groupBy(
          elementRows,
          "dataset.filterGroupRow"
        );

        // Create an entries array out of the groupedElements
        allElements.current = Object.entries(
          groupedElementRows
        ) as ScrollableType;
        // Create a flat version (easier to do operations on everything all at once)
        allElementsFlat.current = allElements.current
          ?.map(([row, group]) => group)
          .flat();

        // Separate rows by the `showLimit`  
        filteredElements = allElements.current.filter(([row, group]) =>
          matchToGroupRow(_showLimit, Number(row), true)
        );
        unfilteredElements = allElements.current.filter(([row, group]) =>
          matchToGroupRow(_showLimit, Number(row), false)
        );
        setElements({ filteredElements, unfilteredElements });
      } else {
        allElements.current = Array.from(
          el.querySelectorAll(`${filterType}`) as HTMLElement[]
        );

        filteredElements = allElements.current.filter((el) =>
          matchToFilter(_activeFilter, el, true)
        );
        unfilteredElements = allElements.current.filter((el) =>
          matchToFilter(_activeFilter, el, false)
        );
        setElements({ filteredElements, unfilteredElements });
      }

      // Process dom nodes (elements)
      if (allElements.current) {
        /**
         * Slider list view
         */
        if (isListView) {
          allElements.current.forEach((group) =>
            group.forEach(
              filterElements(displayType, _showLimit, _activeFilter)
            )
          );
          /**
           * Scrollable Type is NcolGrouped with market selections array length larger than 3
           */
        } else if (isScrollableType) {
          // Process Rows
          if (_activeFilter === "all") {
            if (typeof _showLimit === "number") {
              (allElements.current as ScrollableType).forEach(
                ([row, group]) => {
                  group.forEach(showElements(displayType));
                }
              );
              (unfilteredElements as ScrollableBaseType).forEach(
                ([row, group]) => {
                  Array.isArray(group) &&
                    group.forEach(hideElements(displayType));
                }
              );
            } else {
              (allElements.current as ScrollableType).forEach(
                ([row, group]) => {
                  group.forEach(showElements(displayType));
                }
              );
            }
          } else {
            allElementsFlat.current?.forEach(showElements(displayType));
            allElementsFlat.current
              ?.filter(
                (el) => el.dataset?.filterGroup?.toLowerCase() !== _activeFilter
              )
              .forEach(hideElements(displayType));
          }

          /**
           * NcolGrouped - normal - process individual elements
           */
        } else {
          if (_activeFilter === "all") {
            (allElements.current as HTMLElement[]).forEach(
              filterElements(displayType, _showLimit, _activeFilter)
            );
          } else {
            filteredElements.forEach(
              filterElements(displayType, _showLimit, _activeFilter)
            );
            unfilteredElements.forEach(hideElements(displayType));
          }
        }
      }
      if (
        setFinished &&
        typeof setFinished === "function" &&
        finished.firstTime
      ) {
        setFinished({ firstTime: false, value: true });
      }
    }
  }, [
    activeFilter,
    el,
    showLimit,
    hasFilters,
    groupBy,
    hasBeenToggled,
    mountHiddenMarkets,
  ]);
  return [
    elements.filteredElements,
    elements.unfilteredElements || allElements.current,
  ];
};

function filterElements(
  displayType: React.MutableRefObject<string>,
  _showLimit: "all" | number,
  _activeFilter: string,
  ignoreShowLimit: boolean = false
): (value: HTMLElement, index: number, array: HTMLElement[]) => void {
  return (_el, i) => {
    if (firstRender) {
      displayType.current = window.getComputedStyle(_el).display;
      firstRender = false;
    }
    const $el = createDisplayableElement(_el, displayType.current);

    if (ignoreShowLimit) {
      $el.show();
    }

    const canShowLimit =
      _showLimit !== "all" && typeof _showLimit === "number" && i < _showLimit;
    const canShowElement = _showLimit === "all" || canShowLimit;

    if (canShowElement) {
      $el.show();
    } else {
      $el.hide();
    }
  };
}

function hideElements(
  displayType: React.MutableRefObject<string>
): (value: HTMLElement, index: number, array: HTMLElement[]) => void {
  return (_el, i) => {
    if (firstRender) {
      displayType.current = window.getComputedStyle(_el).display;
      firstRender = false;
    }
    const $el = createDisplayableElement(_el, displayType.current);
    $el.hide();
  };
}

function showElements(
  displayType: React.MutableRefObject<string>
): (value: HTMLElement, index: number, array: HTMLElement[]) => void {
  return (_el, i) => {
    if (firstRender) {
      displayType.current = window?.getComputedStyle(_el).display;
      firstRender = false;
    }
    const $el = createDisplayableElement(_el, displayType.current);
    $el.show();
  };
}

function createDisplayableElement(el, displayType) {
  return {
    show() {
      el.style.display = displayType;
    },
    hide() {
      el.style.display = "none";
    },
  };
}
// match: boolean, if true match to filterGroup, if false, don't match to the filterGroup.
function matchToFilter(activeFilter, el, match) {
  if (!el) return false;
  return match
    ? activeFilter.toLowerCase() === el?.dataset?.filterGroup?.toLowerCase()
    : activeFilter.toLowerCase() !== el?.dataset?.filterGroup?.toLowerCase();
}

function matchToGroupRow(limit, row, match) {
  if (typeof row !== "number") return false;
  let _limit;
  if (typeof limit === "number") _limit = limit - 1;
  return match ? row <= _limit : row > _limit;
}
